export interface BadgeColour {
  name: string;
  group: BadgeColourGroup;
  base: string;
  light: string;
  dark: string;
  grad?: [string, string];
  pale: boolean;
}

export type BadgeColourGroup = 'Metals' | 'Volaby' | 'Brights' | 'Pastels' | 'Deep';

interface ColourOptions {
  light?: string;
  dark?: string;
  grad?: [string, string];
  pale?: boolean;
}

const hex = (h: string) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));

export const mix = (a: string, b: string, t: number) =>
  `#${hex(a)
    .map((v, i) =>
      Math.round(v + (hex(b)[i] - v) * t)
        .toString(16)
        .padStart(2, '0')
    )
    .join('')}`;

const C = (name: string, group: BadgeColourGroup, base: string, o: ColourOptions = {}): BadgeColour => ({
  name,
  group,
  base,
  grad: o.grad,
  pale: !!o.pale,
  light: o.light || mix(base, '#FFFFFF', o.pale ? 0.55 : 0.82),
  dark: o.dark || mix(base, '#000000', 0.45),
});

export const BADGE_COLOURS: Record<string, BadgeColour> = {
  bronze: C('Bronze', 'Metals', '#A8653A', { light: '#F5E4D6', dark: '#5F3418', grad: ['#C98451', '#8E4F27'] }),
  silver: C('Silver', 'Metals', '#8E99A4', { light: '#EEF1F4', dark: '#46505A', grad: ['#B5BEC7', '#75808B'] }),
  gold: C('Gold', 'Metals', '#C99A2E', { light: '#F8EDCB', dark: '#6E5010', grad: ['#E6C056', '#A87A16'] }),
  platinum: C('Platinum', 'Metals', '#6E7D96', { light: '#E9EDF5', dark: '#323C52', grad: ['#9FAEC6', '#56637B'] }),
  rosegold: C('Rose gold', 'Metals', '#C7837A', { light: '#F7E4E0', dark: '#73392F', grad: ['#E3A79D', '#A9645B'] }),

  brunswick: C('Brunswick', 'Volaby', '#0A4439', { light: '#D6ECE5', dark: '#052620' }),
  mulberry: C('Mulberry', 'Volaby', '#6E1C42', { light: '#F4DCE7', dark: '#3E0F25' }),
  tangerine: C('Tangerine', 'Volaby', '#F05223', { light: '#FDE2D8', dark: '#842A0F' }),
  pacific: C('Pacific', 'Volaby', '#0B627F', { light: '#D7ECF3', dark: '#053445' }),
  ultraviolet: C('Ultraviolet', 'Volaby', '#686495', { light: '#E6E5F2', dark: '#363357' }),
  violet: C('Volaby purple', 'Volaby', '#796FFF', { light: '#E6E5FC', dark: '#3A2FB8' }),
  tiffany: C('Tiffany', 'Volaby', '#95DCDC', { pale: true, dark: '#1D6E70' }),
  mint: C('Mint green', 'Volaby', '#BFE3D6', { pale: true, dark: '#2C6B55' }),
  floss: C('Floss', 'Volaby', '#FAD3E4', { pale: true, dark: '#9A2F63' }),
  almond: C('Almond', 'Volaby', '#FBE9D2', { pale: true, dark: '#8A5A2B' }),

  cherry: C('Cherry', 'Brights', '#E23B4E'),
  coral: C('Coral', 'Brights', '#FF6F59', { dark: '#9A2B1B' }),
  orange: C('Mango', 'Brights', '#FF9F1C', { dark: '#8A4B00', pale: true }),
  sunshine: C('Sunshine', 'Brights', '#FFC93C', { pale: true, dark: '#7A5600' }),
  lime: C('Lime', 'Brights', '#9BD443', { pale: true, dark: '#3E6B12' }),
  jungle: C('Jungle', 'Brights', '#1FA67A', { dark: '#0D5A41' }),
  lagoon: C('Lagoon', 'Brights', '#12A4B6', { dark: '#07535D' }),
  ocean: C('Sky', 'Brights', '#2F8CF0', { dark: '#0F4787' }),
  cobalt: C('Cobalt', 'Brights', '#3456D9'),
  grape: C('Grape', 'Brights', '#8E44AD'),
  magenta: C('Magenta', 'Brights', '#D63AAF'),
  bubblegum: C('Bubblegum', 'Brights', '#FF6FB5', { dark: '#9E1F5E' }),

  lavender: C('Lavender', 'Pastels', '#CDB8F5', { pale: true, dark: '#4B2F8F' }),
  peach: C('Peach', 'Pastels', '#FFC9A8', { pale: true, dark: '#A1461A' }),
  lemon: C('Lemon', 'Pastels', '#FFF0A3', { pale: true, dark: '#7A6500' }),
  seafoam: C('Seafoam', 'Pastels', '#B5EAD7', { pale: true, dark: '#1F6B52' }),
  powder: C('Powder', 'Pastels', '#BFDBFE', { pale: true, dark: '#1E4E8C' }),
  blush: C('Blush', 'Pastels', '#F8C8D0', { pale: true, dark: '#9C2E45' }),
  lilac: C('Lilac', 'Pastels', '#E8C5EE', { pale: true, dark: '#7A2E86' }),

  forest: C('Forest', 'Deep', '#2F7D55', { light: '#DDF0E5', dark: '#17472E' }),
  rose: C('Raspberry', 'Deep', '#C2416B', { light: '#F8DFE7', dark: '#6E1B37' }),
  midnight: C('Midnight', 'Deep', '#1E2A5A', { light: '#DFE3F1' }),
  plum: C('Plum', 'Deep', '#4A1D4F', { light: '#EEDDF0' }),
  espresso: C('Espresso', 'Deep', '#4B2E22', { light: '#F0E4DE' }),
  slate: C('Slate', 'Deep', '#3F4A57', { light: '#E4E7EB', dark: '#1E252D' }),
  ink: C('Ink', 'Deep', '#1B1A22', { light: '#E5E4EA', dark: '#000000' }),
};

export const BADGE_COLOUR_GROUPS: BadgeColourGroup[] = ['Metals', 'Volaby', 'Brights', 'Pastels', 'Deep'];
